import { QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import type { UndefinedInitialDataInfiniteOptions } from '@tanstack/react-query/src/infiniteQueryOptions';

import { AppID, Environment, TimePrecision } from '@playq/octopus-common';
import { GenericFailure, TimeOffsetIterator } from '@playq/services-shared';
import { MessagePage, MessageReceiver } from '@playq/services-teams';

import { services2 } from '/api/services2';
import { unwrapEither } from '/api/service-hooks';
import { appToolkit } from '/store';
import { toMs } from '/helpers';

export const INITIAL_READ_MESSAGES_OFFSET = 0;
export const INITIAL_READ_MESSAGES_LIMIT = 100;

export const INITIAL_READ_MESSAGES_ITERATOR: TimeOffsetIterator = new TimeOffsetIterator({
  offset: INITIAL_READ_MESSAGES_OFFSET,
  limit: INITIAL_READ_MESSAGES_LIMIT,
  from: undefined,
  till: undefined,
});

export const readMessagesKeys: QueryKey = ['service', 'support', 'teams', 'chat', 'readMessages'];
const emptyArray: [] = [];
export const useReadMessages = (
  receiver: MessageReceiver,
  timeOffsetIterator: TimeOffsetIterator,
  env: Environment,
  options?: UndefinedInitialDataInfiniteOptions<MessagePage | undefined, GenericFailure | Error>
) => {
  const appID: AppID | undefined = useSelector(appToolkit.selectors.appID);

  const keys = readMessagesKeys.concat(
    appID,
    receiver,
    timeOffsetIterator.serialize(),
    env,
    receiver.channel.getFullClassName()
  );
  const { data, ...res } = useInfiniteQuery<MessagePage | undefined, GenericFailure | Error>({
    queryKey: keys,
    initialPageParam: INITIAL_READ_MESSAGES_ITERATOR,
    queryFn: async ({ pageParam }) => {
      if (!appID) {
        throw new Error('AppID is required');
      }

      const iterator = pageParam !== undefined ? (pageParam as TimeOffsetIterator) : timeOffsetIterator;

      const result = await unwrapEither(services2.teamsChatLookupService.readMessages(appID, receiver, iterator, env));

      return result;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.messages || lastPage.messages.length === 0) {
        return undefined;
      }

      if (lastPage.messages.length < timeOffsetIterator.limit) {
        return undefined;
      }

      const totalMessagesLoaded = allPages.reduce((total, page) => {
        return total + (page?.messages?.length ?? 0);
      }, 0);

      const nextTimeOffsetIterator = new TimeOffsetIterator({
        offset: totalMessagesLoaded,
        limit: timeOffsetIterator.limit,
        from: timeOffsetIterator.from_AsString,
        till: timeOffsetIterator.tillAsString,
      });
      return nextTimeOffsetIterator;
    },
    staleTime: toMs(TimePrecision.Second, 5),
    enabled: !!appID,
    ...options,
  });
  const allMessages = useMemo(() => {
    if (!data?.pages) return emptyArray;
    return data.pages.filter((page): page is MessagePage => !!page?.messages).flatMap((page) => page.messages);
  }, [data?.pages]);

  const pinnedMessages = useMemo(() => {
    const firstPage = data?.pages?.[0];
    return firstPage?.pinned || emptyArray;
  }, [data?.pages]);

  return useMemo(
    () => ({
      ...res,
      messages: allMessages,
      pinned: pinnedMessages,
      existMessages: allMessages.length > 0 || pinnedMessages.length > 0,
    }),
    [allMessages, pinnedMessages, res]
  );
};
